import React, { useState, useEffect, useRef } from 'react';
import Piano from './Piano';
import Progressions from './Progressions';
import Chords from './Chords';
import DropdownSelector from './DropdownSelector';

const AprenderAcordes = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [noteColors, setNoteColors] = useState({});
  const [selectedScale, setSelectedScale] = useState(
    () => localStorage.getItem('selectedScale') || 'C'
  );
  const [showScaleDropdown, setShowScaleDropdown] = useState(false);
  const [position, setPosition] = useState(
    () => localStorage.getItem('pianoPosition') || 'right'
  );
  const [tempoBPM, setTempoBPM] = useState(
    () => parseInt(localStorage.getItem('tempoBPM')) || 60
  );
  const [isTempoMode, setIsTempoMode] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const tempoIntervalRef = useRef(null);
  const activeIndexRef = useRef(0);
  const chordKeysRef = useRef([]);

  const currentScale = Progressions[selectedScale] || {};
  const chordKeys = currentScale.chordOrder || [];
  const currentChords = Chords;

  useEffect(() => {
    // Al cambiar de escala/canción, actualizar el tempo al definido en el objeto
    if (currentScale.tempo) {
      setTempoBPM(currentScale.tempo);
    }
    
    activeIndexRef.current = activeIndex;
    chordKeysRef.current = chordKeys;
  }, [selectedScale, activeIndex, chordKeys, currentScale]);

  const handleChordChange = (index) => {
    setActiveIndex(index);
    setNoteColors(currentChords[chordKeys[index]]?.colors || {});
  };

  const clearColors = () => {
    setActiveIndex(0);
    setNoteColors({});
  };

  const changeScale = (scale) => {
    setSelectedScale(scale);
    localStorage.setItem('selectedScale', scale);
    setShowScaleDropdown(false);
    setActiveIndex(0);
    if (isTempoMode) {
      resetTempo();
    }
  };

  const changePosition = (pos) => {
    setPosition(pos);
    localStorage.setItem('pianoPosition', pos);
  };

  const toggleTempoMode = () => {
    if (isTempoMode) {
      stopTempo();
    } else {
      startTempo();
    }
  };

  const startTempo = () => {
    setIsTempoMode(true);
    setCurrentBeat(0);
    handleChordChange(0);

    tempoIntervalRef.current = setInterval(() => {
      setCurrentBeat(prevBeat => {
        const newBeat = (prevBeat + 1) % 4;
        
        if (newBeat === 0) {
          const nextIndex = (activeIndexRef.current + 1) % chordKeysRef.current.length;
          handleChordChange(nextIndex);
        }
        
        return newBeat;
      });
    }, (60 / tempoBPM) * 1000);
  };

  const stopTempo = () => {
    setIsTempoMode(false);
    setCurrentBeat(0);
    if (tempoIntervalRef.current) {
      clearInterval(tempoIntervalRef.current);
      tempoIntervalRef.current = null;
    }
  };

  const resetTempo = () => {
    stopTempo();
    if (chordKeys.length > 0) {
      handleChordChange(0);
    }
  };

  const increaseTempo = () => {
    const newTempo = tempoBPM + 10;
    setTempoBPM(newTempo);
    localStorage.setItem('tempoBPM', newTempo.toString());
    if (isTempoMode) {
      resetTempo();
      startTempo();
    }
  };

  const decreaseTempo = () => {
    const newTempo = Math.max(10, tempoBPM - 10);
    setTempoBPM(newTempo);
    localStorage.setItem('tempoBPM', newTempo.toString());
    if (isTempoMode) {
      resetTempo();
      startTempo();
    }
  };

  useEffect(() => {
    if (chordKeys.length > 0 && !isTempoMode) {
      handleChordChange(0);
    }
    
    return () => {
      if (tempoIntervalRef.current) {
        clearInterval(tempoIntervalRef.current);
      }
    };
  }, [selectedScale]);

  return (
    <>
      <DropdownSelector 
        selectedScale={selectedScale}
        showScaleDropdown={showScaleDropdown}
        setShowScaleDropdown={setShowScaleDropdown}
        changeScale={changeScale}
      />

      <div className="flex gap-4 mb-[150px] z-30 flex-wrap justify-center">
        {chordKeys.map((chordKey, index) => (
          <button
            key={`${chordKey}-${index}`}
            onClick={() => handleChordChange(index)}
            className={`px-6 py-3 rounded-lg text-4xl font-bold transition-colors
              ${activeIndex === index ? 'bg-blue-500 text-white' : 'bg-white text-gray-800'}`}
          >
            {chordKey.replace('H', '#')}
          </button>
        ))}
      </div>

      {isTempoMode && (
        <div className="flex justify-center gap-8 absolute top-[350px]">
          {[1, 2, 3, 4].map((beat) => (
            <div
              key={beat}
              className={`text-5xl font-bold ${
                beat === currentBeat + 1 ? 'text-yellow-400 scale-150' : 'text-white'
              } transition-transform`}
            >
              {beat}
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-center gap-4 mb-4">
        <button 
          onClick={() => changePosition('left')}
          className={`px-4 py-2 rounded-lg ${position === 'left' ? 'bg-blue-500 text-white' : 'bg-white'}`}
        >
          <i className="fa-solid fa-align-left"></i>
        </button>
        <button 
          onClick={() => changePosition('center')}
          className={`px-4 py-2 rounded-lg ${position === 'center' ? 'bg-blue-500 text-white' : 'bg-white'}`}
        >
          <i className="fa-solid fa-align-center"></i>
        </button>
        <button 
          onClick={() => changePosition('right')}
          className={`px-4 py-2 rounded-lg ${position === 'right' ? 'bg-blue-500 text-white' : 'bg-white'}`}
        >
          <i className="fa-solid fa-align-right"></i>
        </button>

        <span className='border-l border-white h-[39px] w-[1px]'></span>

        <button 
          onClick={toggleTempoMode}
          className={`px-4 py-2 rounded-lg transition-colors ${isTempoMode ? 'bg-green-500 text-white' : 'bg-white'}`}
        >
          <i className={`fa-solid ${isTempoMode ? 'fa-stop' : 'fa-play'}`}></i>
        </button>

        <div className="flex items-center gap-2 bg-white rounded-lg px-2">
          <button 
            onClick={decreaseTempo}
            className="px-2 py-1 text-lg font-bold"
            disabled={tempoBPM <= 10}
          >
            &lt;
          </button>
          <div className="px-2 py-1 text-lg font-bold min-w-[40px] text-center">
            {tempoBPM}
          </div>
          <button 
            onClick={increaseTempo}
            className="px-2 py-1 text-lg font-bold"
          >
            &gt;
          </button>
        </div>

        <span className='border-l border-white h-[39px] w-[1px]'></span>

        <button 
          onClick={clearColors}
          className={`px-4 py-2 rounded-lg bg-white`}
        >
          <i className="fa-solid fa-eraser"></i>
        </button>
      </div>

      <Piano noteColors={noteColors} position={position}/>
    </>
  );
};

export default AprenderAcordes;